/**
 * Money is carried as integer milli-rupiah (1 rupiah = 1000 milli).
 *
 * A single chatbot answer costs tens of rupiah, so whole rupiah would round a
 * month of usage into noise, and floats would drift once thousands of debits
 * are summed. Integers in milli-rupiah keep every debit exact and every sum
 * reproducible, which matters because Saldo AI is client money held in trust
 * (Lampiran III Pasal 8) and must reconcile against the provider invoice.
 */
export type Milli = number;

export const MILLI_PER_RUPIAH = 1000;

export function fromRupiah(rupiah: number): Milli {
  return Math.round(rupiah * MILLI_PER_RUPIAH);
}

export function toRupiah(milli: Milli): number {
  return milli / MILLI_PER_RUPIAH;
}

const wholeRupiah = new Intl.NumberFormat("id-ID", {
  style: "currency",
  currency: "IDR",
  maximumFractionDigits: 0,
});

const preciseRupiah = new Intl.NumberFormat("id-ID", {
  style: "currency",
  currency: "IDR",
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

/**
 * "Rp 150.000" for balances and top-ups; per-message costs below Rp 100 keep
 * two decimals so a Rp 46,12 debit doesn't read as a suspicious "Rp 46".
 */
export function formatRupiah(milli: Milli, options: { precise?: boolean } = {}): string {
  const rupiah = toRupiah(milli);
  const precise = options.precise ?? Math.abs(rupiah) < 100;
  return (precise ? preciseRupiah : wholeRupiah).format(rupiah).replace(/ /g, " ");
}
